import { useCallback } from 'react';
import { Res } from 'api/api.types';
import { Counterparty } from 'pages/invoices/invoices.types';
import { useAxios } from '../axios';
import { CounterpartyQueryParams } from '../invoices/invoices.types';
import {
  Customer,
  GetCustomerParams,
  GetMailParams,
  GetShippingLineParams,
  ShippingLine,
  Storehouse,
  GetStorehousesParams
} from './counterparties.types';

export const useGetCounterpartyById = () => {
  const [{ data, loading }, getCounterpartyByIdReq] = useAxios<{
    result: Counterparty;
  }>({
    method: 'GET',
  });

  const getCounterpartyById = useCallback(
    (id: number) => getCounterpartyByIdReq({ url: `/counterparty/${id}` }),
    [getCounterpartyByIdReq],
  );

  return {
    data: data?.result,
    loading,
    getCounterpartyById,
  };
};

export const useGetCounterparties = () => {
  const [{ data, loading }, getCounterpartiesReq] = useAxios<{
    result: { data: Counterparty[] };
  }>({
    method: 'GET',
  });

  const getCounterparties = useCallback(
    ({
      filterText = '',
      pageNumber = 1,
      resultSize = 25,
      excludePrimary = true,
      withFunctionsOnly = true,
    }: CounterpartyQueryParams) =>
      getCounterpartiesReq({
        url: '/counterparty',
        params: {
          ...(filterText && { filterText }),
          ...(pageNumber && { pageNumber }),
          ...(resultSize && { resultSize }),
          ...{
            excludePrimary,
          },
          ...{
            withFunctionsOnly,
          },
        },
      }),
    [getCounterpartiesReq],
  );

  return {
    counterpartiesData: data?.result.data,
    loading,
    getCounterparties,
  };
};

export const useGetMailAddresses = () => {
  const [{ data, loading }, axiosFetch] = useAxios<Res<string[]>>({
    method: 'GET',
  });

  const getMailAddresses = useCallback(
    ({
      filterText = '',
      pageNumber = 1,
      resultSize = 10,
      counterpartyIds,
      withSystemUsers = false,
    }: GetMailParams) =>
      axiosFetch({
        url: '/counterparty/mailaddresses',
        params: {
          filterText,
          pageNumber,
          resultSize,
          counterpartyIds,
          withSystemUsers,
        },
      }),
    [axiosFetch],
  );

  return {
    data: data?.result,
    loading,
    getMailAddresses,
  };
};

export const useGetShippingLines = () => {
  const [{ data, loading }, axiosFetch] = useAxios<{
    result: { data: ShippingLine[]; count: number };
  }>({
    method: 'GET',
  });

  const getShippingLines = useCallback(
    ({
      filterText = '',
      pageNumber = 1,
      resultSize = 10,
      withSystemUsers = false,
    }: GetShippingLineParams) =>
      axiosFetch({
        url: '/counterparty/shippingline',
        params: {
          filterText,
          pageNumber,
          resultSize,
          withSystemUsers,
        },
      }),
    [axiosFetch],
  );

  return {
    data: data?.result.data,
    loading,
    getShippingLines,
  };
};

export const useGetCustomers = () => {
  const [{ data, loading }, axiosFetch] = useAxios<{
    result: { data: Customer[]; count: number };
  }>({
    method: 'GET',
  });

  const getCustomers = useCallback(
    ({
      filterText = '',
      pageNumber = 1,
      resultSize = 25,
      withSystemUsers = false,
    }: GetCustomerParams) =>
      axiosFetch({
        url: '/counterparty/customer',
        params: {
          filterText,
          pageNumber,
          resultSize,
          withSystemUsers,
        },
      }),
    [axiosFetch],
  );

  return {
    data: data?.result.data,
    loading,
    getCustomers,
  };
};

export const useGetStorehouses = () => {
  const [{ data, loading }, axiosFetch] = useAxios<{
    result: Storehouse[];
  }>({
    method: 'GET',
  });

  const getStorehouses = useCallback((id: number) =>
    ({
      filterText = '',
      pageNumber = 1,
      resultSize = 25,
    }: GetStorehousesParams) =>
      axiosFetch({
        url: `/counterparty/customer/${id}/storehouses`,
        params: {
          filterText,
          pageNumber,
          resultSize,
        },
      }),
    [axiosFetch],
  );

  return {
    data: data?.result,
    loading,
    getStorehouses
  };
};

